/**
 * Note lane drawing (W2-03 note-lane; ARCHITECTURE.md §7.7, DESIGN.md §4.6, §5.3-5.4).
 *
 * Pure Canvas2D painters for the three lane layers. Coordinates are CSS px in the column (the host pre-scales by the
 * device pixel ratio); time flows up (TimeAxis), horizontal position is RPE X through a `LaneXMap` (laneHit.ts).
 *
 *   static   background, the play field band (-675..675; outside it is shaded), the waveform backdrop (quiet and
 *            neutral, inside the field), the beat grid (osu! division colours, bar lines from the meter), the X grid
 *            (RPE "vertical lines"; the centre line stronger), the X ruler band at the top (or as a fixed part, see
 *            `LaneStaticOptions.ruler`) and beat numbers at both edges;
 *   content  note glyphs by shape and skin colour (tap bar, thin drag bar, flick arrow notch, hold head/body/tail),
 *            fakes hatched at 50 %, a chevron under bottom-side notes, the multi-highlight double outline (Phira's
 *            exact rule, asked live), transparent notes as outlines, selection rings, count badges for notes at the
 *            same spot, edge arrows with the X for notes outside the visible X range, ghost notes of other lines
 *            ("also show"), density bands where more than one note falls on a pixel row, empty-state messages;
 *   overlay  hover outline, the lane point marker (preview link), placement ghosts with their length label, the
 *            ghost transform of large drags, the transform box and its handles, the eraser trail and the +X arrow.
 *
 * Colours come from design tokens only (`readLaneTheme`, the same token-then-system-colour fallback as theme.ts).
 * Nothing here keeps state; the lane view (laneColumn.ts) decides what to draw.
 */

import { Beats } from '../../core/time/beat';
import type { NoteId, NoteKind } from '../../core/model/types';
import type { TimeAxis } from '../types';
import { axisContext } from './axis';
import { drawBeatGrid } from './gutterColumn';
import { Glyphs, hatchRect, holdGlyph, noteGlyph, noteGlyphPath, NOTE_GLYPH_H } from './glyphs';
import {
  FIELD_HALF_PX,
  FLAG_BELOW,
  FLAG_FAKE,
  FLAG_TRANSPARENT,
  glyphHalfPx,
  KIND_OF_CODE,
  STACK_EPS_X,
  type LaneNoteIndex,
  type LaneXMap,
} from './laneHit';
import { planLod, type LodPlan } from './lod';
import { canvasFont, FALLBACK_THEME, type TimelineTheme } from './theme';
import { drawWaveform } from './waveformColumn';

// ---------------------------------------------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------------------------------------------

export interface LaneTheme {
  readonly note: Readonly<Record<NoteKind, string>>;
  /** Selection rings and the transform box. */
  readonly selected: string;
  /** Hover outline (also the preview-linked outline). */
  readonly hover: string;
  /** Multi-highlight (simultaneous) outer outline. */
  readonly mh: string;
  /** Edge arrows of off-range notes. */
  readonly offRange: string;
  /** Count badges. */
  readonly badgeBg: string;
  readonly badgeText: string;
  /** Shading outside the play field. */
  readonly outside: string;
  /** X ruler band. */
  readonly ruler: string;
  /** Eraser trail and refused placements. */
  readonly danger: string;
  /** Placement ghost accent (valid placement). */
  readonly ghost: string;
}

const LANE_TOKENS: Readonly<Record<Exclude<keyof LaneTheme, 'note'>, readonly string[]>> = {
  selected: ['--color-accent', 'Highlight'],
  hover: ['--color-focus-ring', '--color-accent-hover', 'Highlight'],
  mh: ['--color-warning', 'Mark'],
  offRange: ['--color-warning', 'Mark'],
  badgeBg: ['--color-surface-3', 'Canvas'],
  badgeText: ['--color-text', 'CanvasText'],
  outside: ['--color-surface-sunken', 'Canvas'],
  ruler: ['--color-surface-1', 'Canvas'],
  danger: ['--color-danger', 'Mark'],
  ghost: ['--color-success', 'Highlight'],
};

const NOTE_TOKENS: Readonly<Record<NoteKind, readonly string[]>> = {
  tap: ['--color-note-tap', '--color-accent', 'Highlight'],
  drag: ['--color-note-drag', '--color-warning', 'Mark'],
  flick: ['--color-note-flick', '--color-danger', 'Mark'],
  hold: ['--color-note-hold', '--color-accent', 'Highlight'],
};

/** Reads a custom property ('' when undefined). */
export type LaneTokenReader = (name: string) => string;

function resolveToken(read: LaneTokenReader, candidates: readonly string[]): string {
  for (const c of candidates) {
    if (!c.startsWith('--')) return c;
    const v = read(c).trim();
    if (v !== '') return v;
  }
  return '';
}

export function laneThemeFromTokens(read: LaneTokenReader): LaneTheme {
  const flat = {} as Record<Exclude<keyof LaneTheme, 'note'>, string>;
  for (const k of Object.keys(LANE_TOKENS) as Array<Exclude<keyof LaneTheme, 'note'>>) {
    flat[k] = resolveToken(read, LANE_TOKENS[k]);
  }
  return Object.freeze({
    ...flat,
    note: Object.freeze({
      tap: resolveToken(read, NOTE_TOKENS.tap),
      drag: resolveToken(read, NOTE_TOKENS.drag),
      flick: resolveToken(read, NOTE_TOKENS.flick),
      hold: resolveToken(read, NOTE_TOKENS.hold),
    }),
  });
}

/** Lane colours from the tokens visible at `el` (system colours without a stylesheet). */
export function readLaneTheme(el: Element | null): LaneTheme {
  if (el === null || typeof getComputedStyle !== 'function') return laneThemeFromTokens(() => '');
  let style: CSSStyleDeclaration | null = null;
  try {
    style = getComputedStyle(el);
  } catch {
    style = null;
  }
  return laneThemeFromTokens((name) => style?.getPropertyValue(name) ?? '');
}

export const FALLBACK_LANE_THEME: LaneTheme = laneThemeFromTokens(() => '');

/** Timeline theme of the host (axis context) or the fallback. */
export function timelineThemeOf(axis: TimeAxis): TimelineTheme {
  return axisContext(axis)?.theme ?? FALLBACK_THEME;
}

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** Height of the X ruler band at the top of the lane, CSS px. */
export const RULER_PX = 16;
/** Edge arrow size of off-range notes. */
const EDGE_ARROW_PX = 7;
/** Opacity of fake notes (DESIGN.md §4.6: hatched at 50 %). */
export const FAKE_ALPHA = 0.5;
/** Opacity of notes on the other side when the sides filter hides them. */
export const FILTERED_ALPHA = 0.15;
/** Opacity of ghost notes of other lines (also show). */
export const GHOST_LINE_ALPHA = 0.35;
/** Minimum spacing of X ruler labels, px. */
const RULER_LABEL_SPACING = 44;
/**
 * The lane waveform backdrop is drawn at the View opacity times this factor (the default opacity 0.4 gives 0.16,
 * UX-031: "about 15-20 % in a neutral darker colour").
 */
export const LANE_WAVEFORM_FACTOR = 0.4;
const LANE_WAVEFORM_DEFAULT_OPACITY = 0.4;

// ---------------------------------------------------------------------------------------------------------------
// X grid
// ---------------------------------------------------------------------------------------------------------------

/** X of grid line i of `count` lines over the field (RPE GRID-02: x_i = -675 + i * 1350 / (count - 1)). */
export function xGridLine(i: number, count: number): number {
  if (count <= 1) return 0;
  return -FIELD_HALF_PX + (i * 2 * FIELD_HALF_PX) / (count - 1);
}

/** Every grid line X (empty for count < 2: only the centre is meaningful then). */
export function xGridLines(count: number): number[] {
  const n = Math.max(0, Math.floor(count));
  if (n < 2) return n === 1 ? [0] : [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(xGridLine(i, n));
  return out;
}

/** Nearest grid line X (RPE AttachX, placement with X snap). */
export function snapToXGrid(x: number, count: number): number {
  const n = Math.max(1, Math.floor(count));
  if (n < 2) return 0;
  const step = (2 * FIELD_HALF_PX) / (n - 1);
  const i = Math.round((x + FIELD_HALF_PX) / step);
  return -FIELD_HALF_PX + i * step;
}

/** Formats an RPE X for labels: integers plain, others with up to one decimal ("-135", "12.5"). */
export function formatX(x: number): string {
  const r = Math.round(x * 10) / 10;
  const v = Object.is(r, -0) ? 0 : r;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

// ---------------------------------------------------------------------------------------------------------------
// Static layer
// ---------------------------------------------------------------------------------------------------------------

export interface LaneStaticOptions {
  readonly theme: TimelineTheme;
  readonly lane: LaneTheme;
  readonly xmap: LaneXMap;
  readonly snapDiv: number;
  readonly xGrid: number;
  readonly xSnap: boolean;
  /** Draw the waveform backdrop (TimelineViewState.waveform). */
  readonly waveform: boolean;
  /** Beat numbers at both edges (whole beats, when there is room). */
  readonly edgeLabels: boolean;
  /**
   * Draw the X ruler band at the top (default true). The lane view draws it as a fixed part instead (TimelineHost
   * scroll cache): the static layer then depends on beats only.
   */
  readonly ruler?: boolean;
}

/** Background, field shading, waveform backdrop, beat grid, X grid, ruler band and edge beat labels. */
export function drawLaneStatic(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  width: number,
  o: LaneStaticOptions,
): void {
  const h = axis.heightPx;
  const { theme, lane, xmap } = o;
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, h);
  // Outside the play field: shaded bands.
  const left = xmap.toPx(-FIELD_HALF_PX);
  const right = xmap.toPx(FIELD_HALF_PX);
  ctx.save();
  ctx.fillStyle = lane.outside;
  ctx.globalAlpha = 0.7;
  if (left > 0) ctx.fillRect(0, 0, Math.min(width, left), h);
  if (right < width) ctx.fillRect(Math.max(0, right), 0, width - Math.max(0, right), h);
  ctx.restore();
  // Waveform backdrop behind the grid (UX-031): a quiet, neutral shape inside the play field (the waveform column
  // next to the lanes is the readable one); the View opacity setting scales it and 0 hides it.
  const ac = axisContext(axis);
  const src = ac?.waveform ?? null;
  if (o.waveform && src !== null) {
    const x0 = Math.max(0, Math.min(width, left));
    const x1 = Math.max(0, Math.min(width, right));
    const opacity = (ac?.view.waveformOpacity ?? LANE_WAVEFORM_DEFAULT_OPACITY) * LANE_WAVEFORM_FACTOR;
    if (x1 - x0 >= 8) drawWaveform(ctx, axis, src, x0, x1 - x0, { color: theme.waveformBackdrop, opacity });
  }
  drawBeatGrid(ctx, axis, width, { div: o.snapDiv, meter: ac?.meter ?? null, theme });
  // X grid (vertical lines), centre line stronger; faint when X snapping is off.
  const lines = xGridLines(o.xGrid);
  ctx.save();
  for (const x of lines) {
    const px = Math.round(xmap.toPx(x));
    if (px < -1 || px > width + 1) continue;
    const centre = Math.abs(x) < 1e-9;
    ctx.globalAlpha = o.xSnap ? (centre ? 0.9 : 0.55) : centre ? 0.5 : 0.25;
    ctx.fillStyle = centre ? theme.gridBar : theme.gridBeat;
    ctx.fillRect(px, 0, 1, h);
  }
  // Field edges.
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = theme.border;
  for (const px of [left, right]) if (px >= 0 && px <= width) ctx.fillRect(Math.round(px), 0, 1, h);
  ctx.restore();
  const ruler = o.ruler !== false;
  if (ruler) drawRuler(ctx, width, o);
  if (o.edgeLabels) drawEdgeBeatLabels(ctx, axis, width, theme, ruler ? RULER_PX + 10 : Number.NEGATIVE_INFINITY);
}

/** X ruler band at the top: grid positions with their RPE X (labels thinned to fit). */
export function drawRuler(ctx: CanvasRenderingContext2D, width: number, o: LaneStaticOptions): void {
  const { theme, lane, xmap } = o;
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = lane.ruler;
  ctx.fillRect(0, 0, width, RULER_PX);
  ctx.globalAlpha = 1;
  ctx.fillStyle = theme.divider;
  ctx.fillRect(0, RULER_PX - 1, width, 1);
  ctx.font = canvasFont(theme, 9, 'mono');
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  const marks = rulerMarks(xmap, o.xGrid);
  let lastRight = Number.NEGATIVE_INFINITY;
  for (const x of marks) {
    const px = xmap.toPx(x);
    if (px < 0 || px > width) continue;
    ctx.fillStyle = theme.textSubtle;
    ctx.fillRect(Math.round(px), RULER_PX - 5, 1, 4);
    const label = formatX(x);
    const half = Math.max(8, label.length * 3);
    if (px - half < lastRight + 4 || px - half < 0 || px + half > width) continue;
    ctx.fillStyle = Math.abs(x) < 1e-9 ? theme.text : theme.textMuted;
    ctx.fillText(label, px, RULER_PX / 2 - 1);
    lastRight = px + half;
  }
  ctx.restore();
}

/** Ruler positions: the X grid when it is sparse enough, else round numbers spaced for the zoom. */
export function rulerMarks(xmap: LaneXMap, xGrid: number): number[] {
  const grid = xGridLines(xGrid);
  const spacing = grid.length >= 2 ? Math.abs((grid[1] ?? 0) - (grid[0] ?? 0)) * xmap.scale : 0;
  if (spacing >= RULER_LABEL_SPACING) {
    // Extend the grid pattern beyond the field when panned or zoomed out.
    const step = Math.abs((grid[1] ?? 0) - (grid[0] ?? 0));
    const lo = xmap.toX(0);
    const hi = xmap.toX(xmap.width);
    const out: number[] = [];
    const k0 = Math.ceil((lo + FIELD_HALF_PX) / step);
    const k1 = Math.floor((hi + FIELD_HALF_PX) / step);
    for (let k = k0; k <= k1 && out.length < 400; k++) out.push(-FIELD_HALF_PX + k * step);
    return out;
  }
  const raw = RULER_LABEL_SPACING / Math.max(1e-9, xmap.scale);
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map((f) => f * pow).find((s) => s >= raw) ?? 10 * pow;
  const lo = Math.ceil(xmap.toX(0) / step);
  const hi = Math.floor(xmap.toX(xmap.width) / step);
  const out: number[] = [];
  for (let k = lo; k <= hi && out.length < 400; k++) out.push(k * step);
  return out;
}

/** Whole-beat numbers at the left and right edges ("13 ---- 13"), none above `minY` (below the ruler band). */
export function drawEdgeBeatLabels(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  width: number,
  theme: TimelineTheme,
  minY = RULER_PX + 10,
): void {
  if (axis.pxPerBeat < 22 || width < 120) return;
  const [b0, b1] = axis.range;
  ctx.save();
  ctx.font = canvasFont(theme, 9, 'mono');
  ctx.fillStyle = theme.textSubtle;
  ctx.textBaseline = 'bottom';
  const first = Math.ceil(b0);
  for (let b = first; b <= b1 && b - first < 2000; b++) {
    const y = axis.yAtBeat(b) - 1;
    if (y < minY) continue;
    const label = String(b);
    ctx.textAlign = 'left';
    ctx.fillText(label, 3, y);
    ctx.textAlign = 'right';
    ctx.fillText(label, width - 3, y);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------------------------
// Content layer: notes
// ---------------------------------------------------------------------------------------------------------------

export interface NoteDrawState {
  readonly selected: boolean;
  /** Multi-highlight (simultaneous; Phira's exact rule). */
  readonly mh: boolean;
  /** Filtered out by the sides filter (drawn faint, not interactive). */
  readonly filtered: boolean;
}

export interface LaneNotesOptions {
  readonly theme: TimelineTheme;
  readonly lane: LaneTheme;
  readonly xmap: LaneXMap;
  /** Per-note state (index position). */
  state(i: number): NoteDrawState;
  /** Whole-lane opacity (locked lines are dimmed). */
  readonly alpha?: number;
  /** Skip these notes (drawn by a ghost transform on the overlay). */
  readonly skip?: (i: number) => boolean;
}

/** Result of a content draw (for tests and diagnostics). */
export interface LaneNotesStats {
  readonly drawn: number;
  readonly banded: number;
  readonly offRange: number;
  readonly badges: number;
}

/** Shape helpers per note, shared by notes, ghosts and paste ghosts. */
export interface GlyphSpec {
  readonly kind: NoteKind;
  readonly cx: number;
  readonly y: number;
  readonly yEnd: number;
  readonly halfW: number;
  readonly fake: boolean;
  readonly below: boolean;
  readonly transparent: boolean;
}

/** Paints one glyph with the lane conventions (colour by kind, hatched fakes, chevron, outline-only transparency). */
export function paintGlyph(
  ctx: CanvasRenderingContext2D,
  g: GlyphSpec,
  lane: LaneTheme,
  opts: { readonly outlineOnly?: boolean } = {},
): void {
  const color = lane.note[g.kind];
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  if (g.fake) ctx.globalAlpha *= FAKE_ALPHA;
  const fill = !(g.transparent || opts.outlineOnly === true);
  if (g.kind === 'hold') holdGlyph(ctx, g.cx, g.y, g.yEnd, g.halfW, { fill, stroke: true });
  else noteGlyph(ctx, g.kind, g.cx, g.y, g.halfW, { fill, stroke: true });
  if (g.fake) {
    // Hatching clipped to the head glyph.
    ctx.save();
    noteGlyphPath(ctx, g.kind, g.cx, g.y, g.halfW);
    ctx.clip();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = lane.badgeText;
    ctx.lineWidth = 1;
    const hh = NOTE_GLYPH_H[g.kind] / 2 + 2;
    hatchRect(ctx, g.cx - g.halfW, g.y - hh, 2 * g.halfW, 2 * hh, 4);
    ctx.restore();
  }
  if (g.below) drawChevron(ctx, g.cx, g.y + NOTE_GLYPH_H[g.kind] / 2 + 2, lane.badgeText);
  ctx.restore();
}

/** The bottom-side chevron ("v") under a glyph. */
export function drawChevron(ctx: CanvasRenderingContext2D, cx: number, top: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = Math.max(ctx.globalAlpha, 0.85);
  ctx.beginPath();
  ctx.moveTo(cx - 4, top);
  ctx.lineTo(cx, top + 4);
  ctx.lineTo(cx + 4, top);
  ctx.stroke();
  ctx.restore();
}

/** Outline around a glyph (`pad` px outside its shape), for selection, hover and the MH double outline. */
export function outlineGlyph(
  ctx: CanvasRenderingContext2D,
  g: GlyphSpec,
  color: string,
  pad: number,
  width: number,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  if (g.kind === 'hold' && g.y !== g.yEnd) {
    const top = Math.min(g.y, g.yEnd) - NOTE_GLYPH_H.hold / 2 - pad;
    const bottom = Math.max(g.y, g.yEnd) + NOTE_GLYPH_H.hold / 2 + pad;
    ctx.strokeRect(g.cx - g.halfW - pad, top, 2 * (g.halfW + pad), bottom - top);
  } else {
    noteGlyphPath(ctx, g.kind, g.cx, g.y, g.halfW + pad, NOTE_GLYPH_H[g.kind] + 2 * pad);
    ctx.stroke();
  }
  ctx.restore();
}

/** Gap (px) between the two rings of the multi-highlight double outline. */
export const MH_RING_GAP = 2.5;

/** The multi-highlight double outline: two concentric 1 px rings, the inner one `pad` px outside the glyph. */
export function drawMhOutline(ctx: CanvasRenderingContext2D, g: GlyphSpec, lane: LaneTheme, pad: number): void {
  outlineGlyph(ctx, g, lane.mh, pad, 1);
  outlineGlyph(ctx, g, lane.mh, pad + MH_RING_GAP, 1);
}

/** Glyph geometry of note i of an index. */
export function glyphOf(index: LaneNoteIndex, i: number, axis: TimeAxis, xmap: LaneXMap): GlyphSpec {
  const flags = index.flags[i] ?? 0;
  return {
    kind: KIND_OF_CODE[index.kind[i] ?? 0] ?? 'tap',
    cx: xmap.toPx(index.x[i] ?? 0),
    y: axis.yAtBeat(index.t[i] ?? 0),
    yEnd: axis.yAtBeat(index.end[i] ?? 0),
    halfW: glyphHalfPx(index.size[i] ?? 1, xmap),
    fake: (flags & FLAG_FAKE) !== 0,
    below: (flags & FLAG_BELOW) !== 0,
    transparent: (flags & FLAG_TRANSPARENT) !== 0,
  };
}

/**
 * Draws the visible notes of a lane. Crowded beat ranges become density bands across the lane (individual glyphs
 * there would overlap anyway); everything else is drawn glyph by glyph in time order (later notes on top).
 */
export function drawLaneNotes(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  index: LaneNoteIndex,
  width: number,
  o: LaneNotesOptions,
): LaneNotesStats {
  if (index.count === 0 || axis.heightPx <= 0) return { drawn: 0, banded: 0, offRange: 0, badges: 0 };
  const { lane, xmap } = o;
  const [b0, b1] = axis.range;
  const first = lowerBound(index.t, b0 - index.maxSpan);
  const last = upperBound(index.t, b1, first);
  // LOD over the visible beats only (held-over holds are always drawn individually).
  const visFirst = lowerBound(index.t, b0, first);
  const plan = planLod(index.t, b0, b1, axis.heightPx, { binPx: 3 });
  let drawn = 0;
  let banded = 0;
  let offRange = 0;
  let badges = 0;
  ctx.save();
  ctx.globalAlpha = o.alpha ?? 1;
  if (plan.bands !== null) {
    const bands = plan.bands;
    ctx.fillStyle = lane.note.tap;
    for (let k = 0; k < bands.bins.length; k++) {
      if (bands.crowded[k] !== 1) continue;
      const beatLo = bands.beat0 + k * bands.binBeats;
      const yBottom = axis.yAtBeat(beatLo);
      const yTop = axis.yAtBeat(beatLo + bands.binBeats);
      Glyphs.densityBand(ctx, 0, width, yTop, yBottom - yTop, (bands.bins[k] ?? 0) / Math.max(1e-9, bands.binPx));
      banded += bands.bins[k] ?? 0;
    }
  }
  for (let i = first; i < last; i++) {
    if (o.skip?.(i) === true) continue;
    const tb = index.t[i] ?? 0;
    const eb = index.end[i] ?? tb;
    if (eb < b0) continue;
    if (i >= visFirst && plan.bands !== null && isBanded(plan, tb)) continue;
    const g = glyphOf(index, i, axis, xmap);
    const st = o.state(i);
    ctx.save();
    if (st.filtered) ctx.globalAlpha *= FILTERED_ALPHA;
    const inView = g.cx + g.halfW >= 0 && g.cx - g.halfW <= width;
    if (inView) {
      paintGlyph(ctx, g, lane);
      if (st.selected) outlineGlyph(ctx, g, lane.selected, 1.5, 2);
      // Multi-highlight: two concentric rings (outside the selection outline when both apply).
      if (st.mh) drawMhOutline(ctx, g, lane, st.selected ? 4 : 2);
      const stack = stackLeader(index, i);
      if (stack > 1) {
        drawCountBadge(ctx, g.cx + g.halfW, g.y, stack, lane, timelineThemeFont(o.theme));
        badges++;
      }
      drawn++;
    } else {
      drawEdgeArrow(ctx, g.cx < 0 ? 'left' : 'right', g.y, width, formatX(index.x[i] ?? 0), lane, o.theme, st.selected);
      offRange++;
    }
    ctx.restore();
  }
  ctx.restore();
  return { drawn, banded, offRange, badges };
}

function timelineThemeFont(theme: TimelineTheme): string {
  return canvasFont(theme, 9, 'sans', 600);
}

function isBanded(plan: LodPlan, beat: number): boolean {
  const bands = plan.bands;
  if (bands === null) return false;
  const k = Math.min(bands.bins.length - 1, Math.max(0, Math.floor((beat - bands.beat0) / bands.binBeats)));
  return bands.crowded[k] === 1;
}

/**
 * Size of the stack note i leads (the first note of a group at the same beat and X), else 0. Used for count badges;
 * O(group size) per note.
 */
export function stackLeader(index: LaneNoteIndex, i: number): number {
  const t = index.t[i] ?? 0;
  const x = index.x[i] ?? 0;
  for (let k = i - 1; k >= 0 && index.t[k] === t; k--) if (Math.abs((index.x[k] ?? 0) - x) < STACK_EPS_X) return 0;
  let n = 1;
  for (let k = i + 1; k < index.count && index.t[k] === t; k++) if (Math.abs((index.x[k] ?? 0) - x) < STACK_EPS_X) n++;
  return n;
}

/** Count badge (a small pill with the number of notes at the same spot). */
export function drawCountBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  count: number,
  lane: LaneTheme,
  font: string,
): void {
  const label = count > 99 ? '99+' : String(count);
  ctx.save();
  ctx.font = font;
  const w = Math.max(12, ctx.measureText(label).width + 6);
  const h = 12;
  const left = x + 2;
  const top = y - h / 2;
  ctx.globalAlpha = 1;
  ctx.fillStyle = lane.badgeBg;
  ctx.strokeStyle = lane.badgeText;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(left, top, w, h, h / 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = lane.badgeText;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, left + w / 2, y + 0.5);
  ctx.restore();
}

/** Edge arrow for a note outside the visible X range, with its X ("<-1215"). */
export function drawEdgeArrow(
  ctx: CanvasRenderingContext2D,
  side: 'left' | 'right',
  y: number,
  width: number,
  label: string,
  lane: LaneTheme,
  theme: TimelineTheme,
  selected: boolean,
): void {
  const a = EDGE_ARROW_PX;
  const tip = side === 'left' ? 1 : width - 1;
  const base = side === 'left' ? tip + a : tip - a;
  ctx.save();
  ctx.fillStyle = selected ? lane.selected : lane.offRange;
  ctx.beginPath();
  ctx.moveTo(tip, y);
  ctx.lineTo(base, y - a / 1.4);
  ctx.lineTo(base, y + a / 1.4);
  ctx.closePath();
  ctx.fill();
  ctx.font = canvasFont(theme, 9, 'mono');
  ctx.textBaseline = 'middle';
  ctx.textAlign = side === 'left' ? 'left' : 'right';
  ctx.fillText(label, side === 'left' ? base + 2 : base - 2, y);
  ctx.restore();
}

/** Notes of other lines drawn as ghosts (dimmed outlines, no interaction). */
export function drawGhostNotes(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  index: LaneNoteIndex,
  width: number,
  xmap: LaneXMap,
  lane: LaneTheme,
): number {
  if (index.count === 0) return 0;
  const [b0, b1] = axis.range;
  const first = lowerBound(index.t, b0 - index.maxSpan);
  const last = upperBound(index.t, b1, first);
  let n = 0;
  ctx.save();
  ctx.globalAlpha = GHOST_LINE_ALPHA;
  ctx.setLineDash([3, 2]);
  for (let i = first; i < last && n < 20000; i++) {
    if ((index.end[i] ?? 0) < b0) continue;
    const g = glyphOf(index, i, axis, xmap);
    if (g.cx + g.halfW < 0 || g.cx - g.halfW > width) continue;
    paintGlyph(ctx, g, lane, { outlineOnly: true });
    n++;
  }
  ctx.restore();
  return n;
}

/** Most wrapped rows one lane message may take; the last row ends in an ellipsis when the text runs longer. */
const MESSAGE_MAX_ROWS = 5;
/** Vertical gap between two messages (title and body), px. */
const MESSAGE_GAP_PX = 4;
/** Horizontal padding on each side of a lane message, px. */
const MESSAGE_PAD_PX = 12;

/** One laid-out row of a lane message. */
export interface LaneMessageRow {
  readonly text: string;
  readonly font: string;
  /** True for the title (first message), drawn in the main text colour; the rest is muted. */
  readonly title: boolean;
  /** Centre of the row, px from the top of the lane. */
  readonly y: number;
  /** Row height, px. */
  readonly height: number;
}

/**
 * Lays out empty-state messages (the first is the title): every message is wrapped to the lane width first, then
 * the rows are stacked with a row height per font size and the whole block is centred on its total height.
 */
export function laneMessageLayout(
  ctx: CanvasRenderingContext2D,
  lines: readonly string[],
  width: number,
  height: number,
  theme: TimelineTheme,
): LaneMessageRow[] {
  if (lines.length === 0 || width <= 0 || height <= 0) return [];
  const maxPx = Math.max(40, width - 2 * MESSAGE_PAD_PX);
  const blocks = lines.map((text, i) => {
    const title = i === 0;
    const size = title ? 12 : 11;
    const font = canvasFont(theme, size, 'sans', title ? 600 : 400);
    ctx.font = font;
    return { font, title, rowH: Math.round(size * 1.35), rows: wrapText(ctx, text, maxPx) };
  });
  const filled = blocks.filter((b) => b.rows.length > 0);
  const total =
    filled.reduce((sum, b) => sum + b.rows.length * b.rowH, 0) + MESSAGE_GAP_PX * Math.max(0, filled.length - 1);
  let y = Math.max(MESSAGE_GAP_PX, (height - total) / 2);
  const out: LaneMessageRow[] = [];
  filled.forEach((b, i) => {
    if (i > 0) y += MESSAGE_GAP_PX;
    for (const text of b.rows) {
      out.push({ text, font: b.font, title: b.title, y: y + b.rowH / 2, height: b.rowH });
      y += b.rowH;
    }
  });
  return out;
}

/** Centred empty-state text: a bold title and muted body messages, each wrapped to the lane width. */
export function drawLaneMessage(
  ctx: CanvasRenderingContext2D,
  lines: readonly string[],
  width: number,
  height: number,
  theme: TimelineTheme,
): void {
  if (lines.length === 0 || width <= 0 || height <= 0) return;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const rows = laneMessageLayout(ctx, lines, width, height, theme);
  // A backdrop plate, so the text stays readable over the grid and the waveform (UX-031).
  let plateW = 0;
  for (const row of rows) {
    ctx.font = row.font;
    plateW = Math.max(plateW, ctx.measureText(row.text).width);
  }
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (first !== undefined && last !== undefined) {
    const pad = 8;
    const w = Math.min(width - 4, plateW + 2 * pad);
    const top = first.y - first.height / 2 - pad / 2;
    const bottom = last.y + last.height / 2 + pad / 2;
    ctx.globalAlpha = 0.88;
    ctx.fillStyle = theme.background;
    ctx.fillRect((width - w) / 2, top, w, bottom - top);
    ctx.globalAlpha = 1;
  }
  for (const row of rows) {
    ctx.font = row.font;
    ctx.fillStyle = row.title ? theme.text : theme.textMuted;
    ctx.fillText(row.text, width / 2, row.y);
  }
  ctx.restore();
}

/** Greedy word wrap with the current font, at most MESSAGE_MAX_ROWS rows; overflow ends in an ellipsis. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxPx: number): string[] {
  const words = text.split(/\s+/).filter((w) => w !== '');
  const rows: string[] = [];
  let cur = '';
  let i = 0;
  while (i < words.length) {
    const w = words[i] ?? '';
    const next = cur === '' ? w : `${cur} ${w}`;
    if (cur !== '' && ctx.measureText(next).width > maxPx) {
      rows.push(cur);
      cur = '';
      if (rows.length >= MESSAGE_MAX_ROWS - 1) break;
      continue;
    }
    cur = next;
    i++;
  }
  const last = [cur, ...words.slice(i)].filter((r) => r !== '').join(' ');
  if (last !== '') rows.push(last);
  return rows.map((r) => fitRow(ctx, r, maxPx));
}

/** The row itself when it fits, else its longest prefix that fits with an ellipsis. */
function fitRow(ctx: CanvasRenderingContext2D, text: string, maxPx: number): string {
  if (ctx.measureText(text).width <= maxPx) return text;
  const ellipsis = '\u2026';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid).trimEnd() + ellipsis).width <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo).trimEnd() + ellipsis;
}

// ---------------------------------------------------------------------------------------------------------------
// Overlay layer
// ---------------------------------------------------------------------------------------------------------------

/** Ghost of a note about to be placed (or a hold being drawn). */
export interface PlacementGhost {
  readonly kind: NoteKind;
  readonly beat: number;
  /** Hold end (beats); equals `beat` for other kinds. */
  readonly end: number;
  readonly x: number;
  readonly size: number;
  readonly fake: boolean;
  readonly below: boolean;
  /** Refused placement (UI mover, locked line): drawn in the danger colour. */
  readonly refused: boolean;
  /** Label next to the ghost (hold length "2 beats · 1.000 s"), already translated. */
  readonly label: string | null;
}

export function drawPlacementGhost(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  xmap: LaneXMap,
  ghost: PlacementGhost,
  lane: LaneTheme,
  theme: TimelineTheme,
): void {
  const g: GlyphSpec = {
    kind: ghost.kind,
    cx: xmap.toPx(ghost.x),
    y: axis.yAtBeat(ghost.beat),
    yEnd: axis.yAtBeat(ghost.end),
    halfW: glyphHalfPx(ghost.size, xmap),
    fake: ghost.fake,
    below: ghost.below,
    transparent: false,
  };
  ctx.save();
  ctx.globalAlpha = 0.55;
  paintGlyph(ctx, g, lane);
  ctx.globalAlpha = 1;
  ctx.setLineDash([4, 3]);
  outlineGlyph(ctx, g, ghost.refused ? lane.danger : lane.ghost, 1.5, 1.5);
  ctx.setLineDash([]);
  if (ghost.label !== null) drawTag(ctx, g.cx + g.halfW + 6, Math.min(g.y, g.yEnd), ghost.label, theme, lane);
  ctx.restore();
}

/** A small label box (lengths, counts) with a solid background. */
export function drawTag(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string,
  theme: TimelineTheme,
  lane: LaneTheme,
): void {
  ctx.save();
  ctx.font = canvasFont(theme, 10, 'sans', 500);
  const w = ctx.measureText(text).width + 8;
  const h = 15;
  const left = Math.max(2, x);
  const top = y - h - 2;
  ctx.globalAlpha = 0.92;
  ctx.fillStyle = lane.badgeBg;
  ctx.fillRect(left, top, w, h);
  ctx.globalAlpha = 1;
  ctx.fillStyle = theme.text;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(text, left + 4, top + h / 2 + 0.5);
  ctx.restore();
}

/** Hover outline of one note (the lane's own hover or the preview link). */
export function drawHoverOutline(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  index: LaneNoteIndex,
  i: number,
  xmap: LaneXMap,
  lane: LaneTheme,
): void {
  if (i < 0 || i >= index.count) return;
  const g = glyphOf(index, i, axis, xmap);
  outlineGlyph(ctx, g, lane.hover, 3, 1.5);
}

/** Lane point marker (preview link): a short horizontal tick at the hovered beat and a vertical hairline at X. */
export function drawLanePoint(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  xmap: LaneXMap,
  beat: number,
  x: number,
  lane: LaneTheme,
): void {
  const px = xmap.toPx(x);
  const y = axis.yAtBeat(beat);
  ctx.save();
  ctx.strokeStyle = lane.hover;
  ctx.globalAlpha = 0.45;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(Math.round(px) + 0.5, RULER_PX);
  ctx.lineTo(Math.round(px) + 0.5, axis.heightPx);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(px - 6, y);
  ctx.lineTo(px + 6, y);
  ctx.stroke();
  ctx.restore();
}

/** Ghost transform of a large drag: the moved notes as outlines shifted by (dBeat, dx), no document change yet. */
export function drawMoveGhost(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  index: LaneNoteIndex,
  indices: readonly number[],
  dBeat: number,
  dx: number,
  width: number,
  xmap: LaneXMap,
  lane: LaneTheme,
): void {
  const shiftY = -dBeat * axis.pxPerBeat;
  const shiftX = dx * xmap.scale;
  ctx.save();
  ctx.globalAlpha = 0.75;
  for (const i of indices) {
    const g0 = glyphOf(index, i, axis, xmap);
    const g = { ...g0, cx: g0.cx + shiftX, y: g0.y + shiftY, yEnd: g0.yEnd + shiftY };
    if (g.cx + g.halfW < 0 || g.cx - g.halfW > width) continue;
    if (Math.max(g.y, g.yEnd) < -20 || Math.min(g.y, g.yEnd) > axis.heightPx + 20) continue;
    paintGlyph(ctx, g, lane, { outlineOnly: true });
    outlineGlyph(ctx, g, lane.selected, 1.5, 1);
  }
  ctx.restore();
}

/** Transform box geometry in lane px (see features/note-lane/transformBox.ts for the math). */
export interface TransformBoxPx {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export type TransformHandle = 'body' | 'top' | 'bottom' | 'left' | 'right';

/** Handle size (px) of the transform box edges. */
export const BOX_HANDLE_PX = 6;

/** Transform box: dashed rectangle with edge handles (time: top/bottom, X: left/right). */
export function drawTransformBox(
  ctx: CanvasRenderingContext2D,
  box: TransformBoxPx,
  active: TransformHandle | null,
  lane: LaneTheme,
): void {
  const w = box.right - box.left;
  const h = box.bottom - box.top;
  if (!(w >= 0 && h >= 0)) return;
  ctx.save();
  ctx.strokeStyle = lane.selected;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 3]);
  ctx.strokeRect(Math.round(box.left) + 0.5, Math.round(box.top) + 0.5, Math.round(w), Math.round(h));
  ctx.setLineDash([]);
  const hs = BOX_HANDLE_PX;
  const midX = (box.left + box.right) / 2;
  const midY = (box.top + box.bottom) / 2;
  const handles: ReadonlyArray<readonly [TransformHandle, number, number, number, number]> = [
    ['top', midX - 2 * hs, box.top - hs / 2, 4 * hs, hs],
    ['bottom', midX - 2 * hs, box.bottom - hs / 2, 4 * hs, hs],
    ['left', box.left - hs / 2, midY - 2 * hs, hs, 4 * hs],
    ['right', box.right - hs / 2, midY - 2 * hs, hs, 4 * hs],
  ];
  for (const [id, x, y, hw, hh] of handles) {
    ctx.fillStyle = id === active ? lane.hover : lane.selected;
    ctx.fillRect(x, y, hw, hh);
  }
  ctx.restore();
}

/** Eraser trail (the pointer path of an eraser drag). */
export function drawEraserTrail(
  ctx: CanvasRenderingContext2D,
  points: readonly (readonly [number, number])[],
  lane: LaneTheme,
): void {
  if (points.length === 0) return;
  ctx.save();
  ctx.strokeStyle = lane.danger;
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  const [x0, y0] = points[0] ?? [0, 0];
  ctx.moveTo(x0, y0);
  for (const [x, y] of points) ctx.lineTo(x, y);
  ctx.stroke();
  ctx.restore();
}

/**
 * The +X arrow: which way +X points on screen at the playhead (the lane draws the line unrotated; the arrow turns
 * with the line). `angle` is the on-screen angle in radians, counter-clockwise from screen right.
 */
export function drawPlusXArrow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  color: string,
): void {
  const r = 6;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(-angle);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r - 3, 0);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(r - 4, -3);
  ctx.lineTo(r - 4, 3);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

function lowerBound(arr: Float64Array, x: number, lo = 0): number {
  let l = lo;
  let h = arr.length;
  while (l < h) {
    const mid = (l + h) >>> 1;
    if ((arr[mid] ?? Number.NaN) < x) l = mid + 1;
    else h = mid;
  }
  return l;
}

function upperBound(arr: Float64Array, x: number, lo = 0): number {
  let l = lo;
  let h = arr.length;
  while (l < h) {
    const mid = (l + h) >>> 1;
    if ((arr[mid] ?? Number.NaN) <= x) l = mid + 1;
    else h = mid;
  }
  return l;
}

/** Length label of a hold being placed: "2 beats · 1.000 s" (parts translated by the caller). */
export function beatsLabel(beats: number): string {
  const b = Beats.fromF64(Math.max(0, beats), 1920);
  return b.n === 0 ? String(b.w) : `${String(b.w)}:${String(b.n)}/${String(b.d)}`;
}

/** Index of the note `id` in `index`, or -1 (convenience for overlays). */
export function indexOfNote(index: LaneNoteIndex, id: NoteId | null): number {
  return id === null ? -1 : index.indexOf(id);
}
